import { inject, Signal, signal } from '@angular/core';
import { BaseListStateService, IFilterControls } from 'src/app/shared/models/base.state.service';
import { AccessGroup, AccessGroupSearchEvent } from '../access-group.model';
import { FormControl, FormGroup } from '@angular/forms';
import {
  catchError,
  combineLatest,
  debounceTime,
  BehaviorSubject,
  distinctUntilChanged,
  map,
  Observable,
  of,
  Subject,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AccessGroupService } from 'src/app/shared/services/access-group.service';
import { PagedResult } from 'src/app/shared/models/paging';
import { AccessGroupCarType } from 'src/app/shared/enums/access-group-car-type';
import { Organization } from '../../organization/organization-models';

interface IAccessGroupsFilterValues {
  query: string | null;
  organizationId: number | null;
  locationIdentifier: string | null;
  selectedAccessGroupIdentifier: string | null;
  selectedConnectorIdentifier: string | null;
  selectedConnectorDescription: string | null;
  selectedCarType: AccessGroupCarType | null;
  sharedDiscountIdentifier: string | null;
}

export enum AccessGroupsQueryParams {
  query = 'query',
  organizationId = 'organizationId',
  locationIdentifier = 'locationIdentifier',
  accessGroupIdentifier = 'accessGroupIdentifier',
  connectorIdentifier = 'connectorIdentifier',
  carType = 'carType',
  sharedDiscountIdentifier = 'sharedDiscountIdentifier',
  selectedConnectorDescription = 'selectedConnectorDescription',
}

export class AccessGroupStateService extends BaseListStateService<AccessGroup, AccessGroupsQueryParams, IAccessGroupsFilterValues> {
  private accessGroupService = inject(AccessGroupService);
  protected createQueryParams(filters: Partial<IAccessGroupsFilterValues>): Record<AccessGroupsQueryParams, string | null> {
    const queryParams: Record<AccessGroupsQueryParams, string | null> = {
      [AccessGroupsQueryParams.locationIdentifier]: filters.locationIdentifier ? encodeURIComponent(filters.locationIdentifier) : null,
      [AccessGroupsQueryParams.query]: filters.query ? encodeURIComponent(filters.query) : null,
      [AccessGroupsQueryParams.accessGroupIdentifier]: filters.selectedAccessGroupIdentifier
        ? encodeURIComponent(filters.selectedAccessGroupIdentifier)
        : null,
      [AccessGroupsQueryParams.selectedConnectorDescription]: filters.selectedConnectorDescription
        ? encodeURIComponent(filters.selectedConnectorDescription)
        : null,
      [AccessGroupsQueryParams.connectorIdentifier]: filters.selectedConnectorIdentifier
        ? encodeURIComponent(filters.selectedConnectorIdentifier)
        : null,
      [AccessGroupsQueryParams.organizationId]: filters.organizationId ? encodeURIComponent(filters.organizationId) : null,
      [AccessGroupsQueryParams.carType]: filters.selectedCarType
        ? this.filteringService.safeCastEnum(AccessGroupCarType, filters.selectedCarType)
        : null,
      [AccessGroupsQueryParams.sharedDiscountIdentifier]: filters.sharedDiscountIdentifier
        ? encodeURIComponent(filters.sharedDiscountIdentifier)
        : null,
    };
    return queryParams;
  }
  resetOrgTrigger = signal<{ reset: boolean }>({ reset: false });
  resetLocationTrigger = signal<{ reset: boolean }>({ reset: false });
  resetDiscountTrigger = signal<{ reset: boolean }>({ reset: false });
  fetchedOrganizations = signal<Organization[]>([]);
  public accessGroups = signal<PagedResult<AccessGroup>>({
    items: [],
    totalCount: 0,
    pageIndex: 0,
    pageSize: this.defaultPageSize,
  });
  private refreshAccessGroupSubject = new Subject<string>();
  private readonly queryParams = this.queryParamMap$();

  focusedAccessGroup = signal<AccessGroup | null>(null);
  form: FormGroup<IFilterControls<IAccessGroupsFilterValues>> = new FormGroup<IFilterControls<IAccessGroupsFilterValues>>({
    query: new FormControl<string | null>(
      this.queryParams?.get(AccessGroupsQueryParams.query)
        ? decodeURIComponent(this.queryParams?.get(AccessGroupsQueryParams.query) ?? '')
        : null
    ),
    selectedConnectorDescription: new FormControl<string | null>(
      this.queryParams?.get(AccessGroupsQueryParams.selectedConnectorDescription)
        ? decodeURIComponent(this.queryParams?.get(AccessGroupsQueryParams.selectedConnectorDescription) ?? '')
        : null
    ),
    organizationId: new FormControl<number | null>(
      this.queryParams?.get(AccessGroupsQueryParams.organizationId)
        ? parseInt(this.queryParams?.get(AccessGroupsQueryParams.organizationId) ?? '')
        : null
    ),
    locationIdentifier: new FormControl<string | null>(this.queryParams?.get(AccessGroupsQueryParams.locationIdentifier) ?? null),
    selectedAccessGroupIdentifier: new FormControl<string | null>(
      this.queryParams?.get(AccessGroupsQueryParams.accessGroupIdentifier) ?? null
    ),
    selectedConnectorIdentifier: new FormControl<string | null>(this.queryParams?.get(AccessGroupsQueryParams.connectorIdentifier) ?? null),
    selectedCarType: new FormControl<AccessGroupCarType | null>(
      this.queryParams?.get(AccessGroupsQueryParams.carType)
        ? this.filteringService.safeCastEnum(AccessGroupCarType, this.queryParams?.get(AccessGroupsQueryParams.carType) ?? undefined)
        : null
    ),
    sharedDiscountIdentifier: new FormControl<string | null>(
      this.queryParams?.get(AccessGroupsQueryParams.sharedDiscountIdentifier) ?? null
    ),
  });

  focusedConnector: Signal<{ identifier: string; description: string } | null> = toSignal(
    this.form.valueChanges.pipe(
      startWith(this.form.value),
      distinctUntilChanged(),
      map(formValues => {
        if (formValues.selectedConnectorIdentifier && formValues.selectedConnectorDescription) {
          return {
            identifier: formValues.selectedConnectorIdentifier,
            description: formValues.selectedConnectorDescription,
          };
        }
        return null;
      })
    ),
    {
      initialValue:
        this.form.controls.selectedConnectorIdentifier.value && this.form.controls.selectedConnectorDescription.value
          ? {
              identifier: this.form.controls.selectedConnectorIdentifier.value,
              description: this.form.controls.selectedConnectorDescription.value,
            }
          : null,
    }
  );

  constructor() {
    super({ active: 'name', direction: 'desc' });
    this.resetPageOnFilterChange();

    this.activatedRoute.queryParamMap
      .pipe(
        map(queryParams => queryParams.get(AccessGroupsQueryParams.sharedDiscountIdentifier)),
        distinctUntilChanged(),
        takeUntilDestroyed()
      )
      .subscribe(sharedDiscountIdentifier => {
        const control = this.form.controls.sharedDiscountIdentifier;
        if (control.value !== sharedDiscountIdentifier) {
          control.setValue(sharedDiscountIdentifier);
        }
      });

    this.$accessGroups.pipe(takeUntilDestroyed()).subscribe(accessGroups => this.accessGroups.set(accessGroups));

    this.refreshAccessGroupSubject
      .pipe(
        tap(identifier => this.highlightAccessGroup(identifier)),
        switchMap(identifier =>
          this.accessGroupService
            .getAccessGroups({
              query: null,
              organizationId: null,
              accessGroupIdentifier: identifier,
              paging: { length: 0, pageIndex: 0, pageSize: 1 },
              sorting: this.viewState().sort,
            })
            .pipe(map(result => result.items[0]))
        ),
        takeUntilDestroyed()
      )
      .subscribe(updatedAccessGroup => {
        if (!updatedAccessGroup) {
          return;
        }

        const currentResult = this.accessGroups();
        const itemIndex = currentResult.items.findIndex(item => item.identifier === updatedAccessGroup.identifier);
        if (itemIndex === -1) {
          return;
        }

        const highlight = currentResult.items[itemIndex].highlight;
        const updatedItems = [...currentResult.items];
        updatedItems[itemIndex] = { ...updatedAccessGroup, highlight };
        this.accessGroups.set({ ...currentResult, items: updatedItems });

        setTimeout(() => {
          const latestResult = this.accessGroups();
          const latestIndex = latestResult.items.findIndex(item => String(item.identifier) === String(updatedAccessGroup.identifier));
          if (latestIndex === -1) {
            return;
          }

          const itemsWithoutHighlight = [...latestResult.items];
          itemsWithoutHighlight[latestIndex] = {
            ...itemsWithoutHighlight[latestIndex],
            highlight: new BehaviorSubject<boolean>(false),
          };
          this.accessGroups.set({ ...latestResult, items: itemsWithoutHighlight });
        }, 3000);
      });
  }

  private highlightAccessGroup(identifier: string) {
    const currentResult = this.accessGroups();
    const itemIndex = currentResult.items.findIndex(item => String(item.identifier) === String(identifier));
    if (itemIndex === -1) {
      return;
    }

    const updatedItems = [...currentResult.items];
    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      highlight: new BehaviorSubject<boolean>(true),
    };
    this.accessGroups.set({ ...currentResult, items: updatedItems });
  }

  $accessGroups: Observable<PagedResult<AccessGroup>> = combineLatest([
    toObservable(this.refreshTrigger),
    this.form.valueChanges.pipe(startWith(this.form.value)),
    toObservable(this.viewState),
  ]).pipe(
    takeUntilDestroyed(),
    debounceTime(300),
    distinctUntilChanged(),
    tap(() => this.loading.set(true)),
    tap(([_, form]) => {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: this.createQueryParams(form),
        queryParamsHandling: 'merge',
      });
    }),
    switchMap(([refresh, filters, viewState]) => {
      const searchEvent = <AccessGroupSearchEvent>{
        query: filters.query,
        organizationId: filters.organizationId,
        paging: viewState.page,
        sorting: viewState.sort,
        accessGroupIdentifier: filters.selectedAccessGroupIdentifier,
        connectorIdentifier: filters.selectedConnectorIdentifier,
        sharedAccessGroupDiscountIdentifier: filters.sharedDiscountIdentifier,
        chargingStationLocationIdentifier: filters.locationIdentifier,
        carType: filters.selectedCarType,
      };

      return this.accessGroupService.getAccessGroups(searchEvent).pipe(
        map(accessGroups => ({ accessGroups, filters })),
        catchError(() =>
          of({
            accessGroups: { items: [], totalCount: 0, pageIndex: 0, pageSize: this.defaultPageSize } as PagedResult<AccessGroup>,
            filters,
          })
        )
      );
    }),
    tap(({ accessGroups, filters }) => {
      if (filters.selectedAccessGroupIdentifier && accessGroups.items.length === 1) {
        this.focusedAccessGroup.set(accessGroups.items[0]);
      }
      this.loading.set(false);
    }),
    map(({ accessGroups }) => accessGroups)
  );

  readonly organizationChanged$ = combineLatest([
    this.form.controls.organizationId.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed()),
  ]).pipe(
    switchMap(([orgId]) => {
      this.resetOrgTrigger.set({ reset: false });
      return of(orgId);
    })
  );

  public resetFilter() {
    this.ToFirstPage({
      query: null,
      organizationId: null,
      locationIdentifier: null,
      selectedAccessGroupIdentifier: null,
      selectedConnectorIdentifier: null,
      selectedCarType: null,
      sharedDiscountIdentifier: null,
      selectedConnectorDescription: null,
    });
    this.resetOrgTrigger.set({ reset: true });
    this.resetLocationTrigger.set({ reset: true });
    this.resetDiscountTrigger.set({ reset: true });
    this.focusedAccessGroup.set(null);
    this.fetchedOrganizations.set([]);
  }

  public triggerUpdateWithHighlight(identifier: string) {
    this.refreshAccessGroupSubject.next(identifier);
  }

  public filterToAccessGroup(identifier: string) {
    this.ToFirstPage({
      query: null,
      organizationId: null,
      locationIdentifier: null,
      selectedAccessGroupIdentifier: identifier,
      selectedConnectorIdentifier: null,
      selectedCarType: null,
      sharedDiscountIdentifier: null,
      selectedConnectorDescription: null,
    });
    this.resetOrgTrigger.set({ reset: true });
    this.resetLocationTrigger.set({ reset: true });
    this.resetDiscountTrigger.set({ reset: true });
    this.fetchedOrganizations.set([]);
  }
}
